import { Component, type ErrorInfo, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { frontend_errors_path } from "@/routes";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Catches render crashes anywhere in the page tree (INCO-256): shows a
// small recover screen instead of a white page, and reports the error to
// /frontend_errors so it lands in the server log + Airbrake.
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const token =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") ?? "";
    void fetch(frontend_errors_path(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        Accept: "application/json",
      },
      body: JSON.stringify({
        message: error.message,
        stack: error.stack,
        component_stack: info.componentStack,
        url: window.location.href,
      }),
    }).catch(() => {
      // Reporting is best-effort; never let it cascade.
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center gap-4 p-6">
        <h1 className="text-2xl font-medium tracking-tight">
          Something went wrong
        </h1>
        <p className="max-w-md text-center text-sm text-muted-foreground">
          The page hit an unexpected error.
        </p>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </div>
    );
  }
}
